"use client";

import { useRef } from "react";
import Image from "next/image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { SplitText } from "gsap/dist/SplitText";
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { cn } from "@/lib/utils";
import { GroqImage } from "@/helpers/Image";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import HeroPill from "@/components/getting-started/HeroPill";
import BlackPill from "@/components/getting-started/BlackPill";
import { CTAButton } from "@/shadcn/ui/button";

import { PageHomePhemexSchema } from "@/sanity/pages/PageHomePhemexTemplate";
import { PagePhemexOfferSchema } from "@/sanity/pages/PagePhemexOfferTemplate";

import logoImg from "@/public/lp2/global/logo.svg";
import griBGimg from "@/public/lp2/hero/grid-bg.svg";

import phemexLogoImg from "@/public/lp2/global/logo-phemex.svg";
import arrow from "@/public/lp2/hero/arrow.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(useGSAP, ScrollTrigger, SplitText);
}

export default function Hero({
  data,
  hideMission,
}: {
  data: PageHomePhemexSchema | PagePhemexOfferSchema;
  hideMission?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const containerRef2 = useRef<HTMLDivElement | any>(undefined);
  const splitText1Ref = useRef<HTMLParagraphElement | any>(undefined);
  const splitText2Ref = useRef<HTMLParagraphElement | any>(undefined);

  useGSAP(
    () => {
      const originalVideoTop = gsap.getProperty("#video-bg", "top");

      gsap.set("#video-bg", { top: 0 });
      gsap.set(containerRef.current, { visibility: "visible" });

      const heroTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.4 },
      });

      heroTL
        .from("#grid-bg", {})
        .from("#video-bg", {
          duration: 0.4,
        })
        .from("#texts-div > *", {
          stagger: 0.1,
          delay: 0.25,
          duration: 0.3,
        })
        .to(
          "#video-bg",
          {
            opacity: 1,
            top: `${originalVideoTop}px`,
            clearProps: "all",
          },
          "<"
        )
        .from("#pills-div-1 > *", {
          stagger: 0.1,
        })
        .from(
          "#pills-div-2 > *",
          {
            stagger: 0.1,
            duration: 0.3,
          },
          "<"
        )
        .from(
          "#tabs-div",
          {
            duration: 0.45,
            clearProps: "all",
          },
          "-=0.4"
        );
    },
    { scope: containerRef }
  );

  useGSAP(
    () => {
      if (!containerRef2.current) return;

      const heroTL2 = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      heroTL2.from(containerRef2.current.children, {
        y: "3rem",
        stagger: 0.1,
        delay: 0.25,
        scrollTrigger: {
          trigger: containerRef2.current,
          end: "center center",
          scrub: 1,
        },
      });

      const splitedText1 = new SplitText(splitText1Ref.current, {
        type: "words",
      });

      const splitedText2 = new SplitText(splitText2Ref.current, {
        type: "words",
        charsClass:
          "underline underline-offset-4 decoration-slate-100 decoration-1",
      });

      gsap.from(splitedText1.words, {
        autoAlpha: 0.2,
        stagger: 0.08,
        delay: 1,
        scrollTrigger: {
          trigger: splitText1Ref.current,
          end: "center center",
          scrub: 2,
        },
      });

      gsap.from(splitedText2.words, {
        autoAlpha: 0.2,
        stagger: 0.1,
        delay: 1.5,
        scrollTrigger: {
          trigger: splitText2Ref.current,
          end: "top center",
          scrub: 2,
        },
      });
    },
    { scope: containerRef2 }
  );

  const swiperRef = useRef(null);
  const sliderBtnRefs = useRef<HTMLButtonElement[]>([]);

  function goToSlide(index: number) {
    // @ts-ignore
    swiperRef.current.swiper.slideTo(index);
  }

  function changeSliderIndicatorActive(index: number) {
    sliderBtnRefs.current.forEach((ref, i) => {
      if (!ref) return;
      if (i === index) {
        ref.classList.add("active");
      } else {
        ref.classList.remove("active");
      }
    });
  }

  function handleSliderChange(swiper: SwiperClass) {
    changeSliderIndicatorActive(swiper.realIndex);
    scrollIntoView(swiper.realIndex);
  }

  function scrollIntoView(index: number) {
    sliderBtnRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }

  return (
    <>
      <section
        ref={containerRef}
        id="hero" // To set hidden initial state with global CSS
        className={cn(
          "w-full relative overflow-hidden bg-[#000C08] mix-blend-lighten",
          "pt-[13rem] lg:pt-[15rem]"
        )}
      >
        {/* Backgrounds */}
        <Image
          id="grid-bg"
          src={griBGimg}
          alt="Grid background"
          className="absolute z-[-1] top-0 left-0 right-0 h-full w-full object-cover pointer-events-none mix-blend-lighten"
        />

        <HLSVideoPlayer
          id="video-bg"
          controls={false}
          playsInline={true}
          autoPlay={true}
          muted={true}
          loop={true}
          className="absolute z-[-2] top-[28%] lg:top-[25.25rem] left-0 right-0 h-[50%] lg:h-auto w-full !max-w-[1920px] mx-auto object-cover pointer-events-none mix-blend-lighten"
          source={{
            embed: false,
            muxVideo: {
              assetId: "",
              filename: "video-bg",
              status: "ready",
              playbackId: "lmI777ubRl3wQMn1UiNAFSW00M5rD4m01n3jyOfO4rprc",
            },
            embedUrl: "",
            thumbnail: {
              src: "",
              width: 0,
              height: 0,
              lqip: "",
              name: "",
            },
          }}
        />

        {/* End Backgrounds */}

        <div className="container-lp mx-auto relative">
          <div className="hidden lg:flex absolute top-0 left-0 right-0 pt-[16rem] w-full flex-col items-center justify-between gap-[20rem]">
            <div
              id="pills-div-1"
              className="w-full flex items-center justify-between px-[2rem]"
            >
              <HeroPill
                text={data?.heroPills?.[0]?.text}
                className="hero-pill-1"
              />

              <HeroPill
                text={data?.heroPills?.[1]?.text}
                className="hero-pill-2"
              />
            </div>
            <div
              id="pills-div-2"
              className="w-full flex items-center justify-between px-[10rem]"
            >
              <HeroPill
                text={data?.heroPills?.[2]?.text}
                className="hero-pill-3"
              />

              <HeroPill
                text={data?.heroPills?.[3]?.text}
                className="hero-pill-4"
              />
            </div>
          </div>

          <div className="flex flex-col items-center justify-center mx-auto relative z-[1]">
            <div
              id="texts-div"
              className="w-full md:w-4/5 lg:w-[min(108rem,60%)] mb-[18rem] lg:mb-[24rem] flex flex-col items-center justify-center"
            >
              {data?.heroTitles && (
                <PortableText
                  value={data?.heroTitles ?? []}
                  components={customRichTextComponent({
                    block: {
                      h1: ({ children }) => (
                        <h1 className="gradient-title text-[5rem] lg:text-[8rem] font-[600] tracking-[-0.325rem] lg:tracking-[-0.4rem] leading-[110%] text-center">
                          {children}
                        </h1>
                      ),
                      h2: ({ children }) => (
                        <div className="bg-[#37D201]/10 border border-[#37D201] rounded-[.675rem] p-[1rem] sm:px-[2rem] mb-[4rem]">
                          <p className="text-[#37D201] text-center text-[2rem] font-[500] mb-0">
                            {children}
                          </p>
                        </div>
                      ),
                      normal: ({ children }) => (
                        <p className="text-[2rem] font-[500] leading-[3.2rem] text-[#DADADA] opacity-80 lg:opacity-60 text-center mt-[2rem] mb-[3rem] lg:mb-[3.5rem] max-w-[95rem] px-[2rem] lg:px-0">
                          {children}
                        </p>
                      ),
                    },
                    marks: {
                      orangeFlare: ({ children }) => (
                        <span className="phemex-shadow">{children}</span>
                      ),
                    },
                  })}
                />
              )}

              {data?.heroCTA && <CTAButton data={data?.heroCTA} />}
            </div>

            <div id="tabs-div" className="relative">
              <div className="hero-mobile-tabs-div max-w-[100vw] px-[2rem] overflow-x-scroll mb-[1rem] lg:mb-[3rem] flex justify-start md:justify-center content-center gap-[0.5rem]">
                {data?.heroImages?.map((tab, index) => (
                  <button
                    key={index}
                    ref={(el) => {
                      if (el) sliderBtnRefs.current[index] = el;
                    }}
                    onClick={() => goToSlide(index)}
                    className={`${
                      index === 0 ? "active " : ""
                    }button-custom min-w-max py-[1.8rem] px-[3.2rem] rounded-[1.25rem] text-[#fff] text-[2rem] font-[600] leading-[120%] uppercase transition duration-300 ease-in-out`}
                  >
                    {tab.title}
                  </button>
                ))}
              </div>

              <div className="hero-charts-div relative">
                <Swiper
                  ref={swiperRef}
                  centeredSlides={true}
                  onRealIndexChange={handleSliderChange}
                  navigation={true}
                  modules={[Navigation]}
                  slidesPerView={"auto"}
                  className="relative w-full max-w-[95vw] rounded-[3rem] overflow-hidden"
                >
                  {data?.heroImages?.map((tab, index) => (
                    <SwiperSlide key={index} className="w-full shrink-0">
                      {/* DESKTOP */}
                      <div className="hidden lg:block black-blur w-fit rounded-[3rem] mx-auto">
                        <Image
                          src={tab.image}
                          alt={tab.title}
                          className="mx-auto"
                        />
                      </div>
                      {/* MOBILE */}
                      <div className="lg:hidden black-blur w-fit rounded-[3rem] mx-auto">
                        <Image
                          src={tab.imageMobile}
                          alt={tab.title}
                          className="mx-auto"
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
          </div>
        </div>
        {/* fade to next section */}
        <div className="absolute z-[1] left-0 right-0 bottom-0 hero-charts-gradient"></div>
      </section>

      <section className="w-full pb-[5rem] lg:pb-[20rem]">
        <div className="container-lp px-[1rem] lg:w-[min(192rem,100%)] lg:max-w-[192rem] mx-auto pt-[3rem] lg:pt-[10rem]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-y-[2rem]">
            <div className="flex flex-col items-center lg:items-start text-center lg:text-start relative">
              <Image
                className="!w-[30rem] lg:!w-[40rem] !min-w-[30rem] lg:!min-w-[40rem] mb-[1.5rem]"
                alt="Logo"
                src={phemexLogoImg}
              />

              {data?.graphTexts && (
                <PortableText
                  value={data?.graphTexts ?? []}
                  components={customRichTextComponent({
                    block: {
                      normal: ({ children }) => (
                        <p className="font-semibold text-white text-opacity-70 text-[3.5rem] sm:text-[4rem] lg:text-[6rem] leading-[130%] mb-[3rem] lg:mb-[4rem] tracking-tight">
                          {children}
                        </p>
                      ),
                      h6: ({ children }) => (
                        <h6 className="text-[2.5rem] sm:text-[3rem] text-white font-medium">
                          {children}
                        </h6>
                      ),
                    },
                    marks: {
                      orangeFlare: ({ children }) => (
                        <span className="phemex-shadow">{children}</span>
                      ),
                      underline: ({ children }) => (
                        <p className="font-semibold text-white text-[3.5rem] sm:text-[4rem] lg:text-[6rem] leading-[1.3em] tracking-tight mb-[3rem]">
                          {children}
                        </p>
                      ),
                      strong: ({ children }) => (
                        <span className="relative inline-block bg-[#39D105] bg-opacity-[.06] border border-[#39D10566] px-[1rem]">
                          {children}
                          <span className="absolute inline-block w-[.3rem] h-[.3rem] bg-[#39D105] right-full bottom-full"></span>
                          <span className="absolute inline-block w-[.3rem] h-[.3rem] bg-[#39D105] right-full top-full"></span>
                          <span className="absolute inline-block w-[.3rem] h-[.3rem] bg-[#39D105] left-full bottom-full"></span>
                          <span className="absolute inline-block w-[.3rem] h-[.3rem] bg-[#39D105] left-full top-full"></span>
                        </span>
                      ),
                    },
                  })}
                />
              )}

              <Image
                className="absolute bottom-0 left-full !w-[21rem] !min-w-[21rem] -translate-x-1/2 hidden lg:block"
                alt="Arrow"
                src={arrow}
              />
            </div>

            <div className="flex flex-col items-center lg:items-end justify-end">
              {data?.graphImage && (
                <GroqImage
                  className="!w-[min(80.5rem,100%)]"
                  alt="Graph"
                  src={data?.graphImage}
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {!hideMission && (
        <section className="w-full">
          <div className="container-lp mx-auto">
            <div
              ref={containerRef2}
              className="flex flex-col items-center justify-between w-full lg:w-[81%] mx-auto lg:pt-[5rem] lg:pb-[8rem]"
            >
              <Image src={logoImg} alt="Logo" className="" />

              {/* @ts-ignore */}
              {data?.missionTitles && (
                <PortableText
                  // @ts-ignore
                  value={data?.missionTitles ?? []}
                  components={customRichTextComponent({
                    block: {
                      h6: ({ children }) => (
                        <BlackPill
                          text={children as string}
                          className="uppercase mb-[3rem]"
                        />
                      ),
                      normal: ({ children }) => (
                        <p
                          ref={splitText1Ref}
                          className="text-white text-center text-[3.5rem] lg:text-[5rem] font-[600] tracking-[-.325rem] lg:leading-[6.5rem] mb-[3rem] lg:mb-[5rem]"
                        >
                          {children}
                        </p>
                      ),
                      h5: ({ children }) => (
                        <p
                          ref={splitText2Ref}
                          className="text-white text-center text-[3.5rem] lg:text-[5rem] font-[600] tracking-[-.325rem] lg:leading-[6.5rem]"
                        >
                          {children}
                        </p>
                      ),
                    },
                  })}
                />
              )}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
